// Optimized custom JS to apply currency conversion to quickview and cart-drawer

/**
 * Improved performance through:
 * - DOM element caching
 * - Consolidated mutation observers
 * - Event delegation
 * - Optimized debounce and throttle functions
 * - Network request throttling
 * - Observer cleanup
 */

// Enhanced utility functions
const utils = {
  // Improved debounce with proper this binding and argument passing
  debounce(fn, delay) {
    let timer = null;
    return function(...args) {
      const context = this;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(context, args), delay);
    };
  },
  
  // Add throttle function to limit frequent calls
  throttle(fn, delay) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        fn.apply(this, args);
      }
    };
  },
  
  // Format money consistently
  // theme amendment - optional showCurrencyCode param (mirrors money_with_currency vs money in
  // Liquid) for the EU cart breakdown rows in computeEuCartBreakdown(), which need to match
  // whichever format the server-rendered figures next to them are using
  formatMoney(amount, showCurrencyCode = false) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
      currencyDisplay: showCurrencyCode ? 'code' : 'symbol',
    }).format(amount / 100);
  },
  // end theme amendment

  // Check if an element exists in DOM
  elementExists(selector) {
    return document.querySelector(selector) !== null;
  },
  
  // Get cookie utility
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  },
  
  // Set cookie utility
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days*24*60*60*1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  },

  // theme amendment - localized root path for Shopify's Ajax API. Every Ajax API request
  // (/cart.js, /cart/clear.js, /cart/add.js, ...) MUST be prefixed with the storefront root
  // Shopify itself rendered for the current request - `window.Shopify.routes.root`, or the
  // Liquid-rendered `window.routes.root_url` from snippets/inject-routes.liquid. On a store with
  // more than one published locale/market (this one publishes de/es/fr/it alongside en) that root
  // is NOT always "/": it carries the locale/market prefix for the localization the buyer is
  // currently browsing in. Calling a bare "/cart.js" bypasses that prefix, so Shopify resolves the
  // request against the store's DEFAULT market (GB/GBP here) and re-issues the buyer's
  // localization cookies for that default market on the response - silently switching the buyer
  // back to the UK/GBP localization for every subsequent page load. The base theme never has this
  // problem because its own CartAPI (main-a17IIrZU.js) routes everything through
  // `window.routes.*` / `routes.root_url + "cart.js"`, which Liquid always renders localized.
  shopifyRoot() {
    let root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
               (window.routes && window.routes.root_url) ||
               '/';
    if (typeof root !== 'string' || !root) root = '/';
    if (root.charAt(0) !== '/') root = '/' + root;
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  },

  // Builds a localization-safe Ajax API URL, e.g. cartRoute('cart.js') -> "/de/cart.js"
  cartRoute(path) {
    return utils.shopifyRoot() + String(path).replace(/^\/+/, '');
  }
  // end theme amendment
};

// Google Analytics menu tracking utility
const menuTracking = {
  // Detect device type
  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'Tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'Mobile';
    }
    return 'Desktop';
  },
  
  // Get current page
  getCurrentPage() {
    const pathname = window.location.pathname;
    // Clean up the pathname for better readability
    if (pathname === '/' || pathname === '') {
      return 'Home';
    }
    // Remove leading/trailing slashes and return
    return pathname.replace(/^\/+|\/+$/g, '');
  },
  
  // Get location code
  getLocationCode(location) {
    const locationMap = {
      'header_desktop': 'Header',
      'mobile_drawer': 'Drawer',
      'footer': 'Footer',
      'announcement_bar_help': 'Announce'
    };
    return locationMap[location] || location;
  },
  
  trackLink(link, location) {
    if (link.dataset.gaTracked) return;
    link.dataset.gaTracked = 'true';
    
    link.addEventListener('click', function(e) {
      if (typeof gtag !== 'undefined') {
        const menuTitle = this.getAttribute('menu-title') || 
                         this.getAttribute('aria-label') ||
                         this.textContent.trim();
        
        const href = this.getAttribute('href');
        const deviceType = menuTracking.getDeviceType();
        const currentPage = menuTracking.getCurrentPage();
        const locationCode = menuTracking.getLocationCode(location);
        
        // Format: DeviceType - CurrentPage - LocationCode - Title - URL
        const eventLabel = `${deviceType} - ${currentPage} - ${locationCode} - ${menuTitle} _ ${href}`;
        
        gtag('event', 'menu_click', {
          'event_label': eventLabel
        });
      }
    });
  },
  
initializeTracking() {
  // Header menu links - keep all header selectors
  const desktopLinks = document.querySelectorAll('.header__nav a[href], .header__dropdown a[href], .header__wide-menu a[href], .header__menu a[href]');
  const mobileLinks = document.querySelectorAll('.drawer-menu a[href]');
  
  // Footer links
  const footerLinks = document.querySelectorAll('.footer a[href]');
  
  // Announcement bar links - be VERY specific
  const announcementBlogLink = document.querySelectorAll('.announcement-bar .announcement-link.blog[href]');
  const announcementHelpLinks = document.querySelectorAll('.announcement-bar .help-links a[href]');
  
  // Track desktop links, but EXCLUDE announcement bar links
  desktopLinks.forEach(link => {
    // Only track if NOT inside announcement bar
    if (!link.closest('.announcement-bar')) {
      this.trackLink(link, 'header_desktop');
    }
  });
  
  mobileLinks.forEach(link => this.trackLink(link, 'mobile_drawer'));
  footerLinks.forEach(link => this.trackLink(link, 'footer'));
  
  // Track announcement bar links separately
  announcementBlogLink.forEach(link => this.trackLink(link, 'announcement_bar_help'));
  announcementHelpLinks.forEach(link => this.trackLink(link, 'announcement_bar_help'));
}
};

// Centralized cart data management
const cartManager = {
  _cartData: null,
  _fetchPromise: null,
  _lastFetchTime: 0,
  _minFetchInterval: 500,
  _callbacks: [],
  
  // Fetch cart data with throttling
  fetchCartData() {
    const now = Date.now();
    
    // If we have recent data, use it
    if (this._cartData && now - this._lastFetchTime < this._minFetchInterval) {
      return Promise.resolve(this._cartData);
    }
    
    // If a fetch is already in progress, return that promise
    if (this._fetchPromise) {
      return this._fetchPromise;
    }
    
    // Otherwise, fetch fresh data
    // theme amendment - localized Ajax API path (see utils.cartRoute). This call is the first
    // thing handleCartDrawerOpen() does, so a bare "/cart.js" here was the single request that
    // knocked an EU customer's localization back to the default GB/GBP market the moment they
    // opened the cart drawer - and it stayed knocked back for every page load afterwards.
    this._fetchPromise = fetch(utils.cartRoute('cart.js'))
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok ' + response.statusText);
        }
        return response.json();
      })
      .then(cart => {
        this._cartData = cart;
        this._lastFetchTime = Date.now();
        this._notifyCallbacks();
        this._fetchPromise = null;
        return cart;
      })
      .catch(error => {
        this._fetchPromise = null;
        throw error;
      });
    
    return this._fetchPromise;
  },
  
  // Register callback for cart updates
  onCartUpdate(callback) {
    this._callbacks.push(callback);
    if (this._cartData) {
      callback(this._cartData);
    }
  },
  
  // Notify all registered callbacks about cart updates
  _notifyCallbacks() {
    if (!this._cartData) return;
    this._callbacks.forEach(callback => callback(this._cartData));
  }
};

// Currency conversion management
const currencyManager = {
  // theme amendment - refresh() now drives the in-theme currency converter
  // (assets/currency-converter.js) instead of the retired Nova/Booster app's window.baCurr -
  // every call site below (cart drawer, quickview, variant changes, AJAX updates, etc.) is
  // unchanged, only this internal hook was swapped.
  refresh(retries = 5, delay = 200) {
    if (typeof window.themeCurrencyConverter !== 'undefined' && typeof window.themeCurrencyConverter.convertAll === 'function') {
      window.themeCurrencyConverter.convertAll();
      return true;
    } else if (retries > 0) {
      setTimeout(() => this.refresh(retries - 1, delay), delay);
      return false;
    }
    return false;
  },
  // end theme amendment

  refreshWithDelay(delay = 750) {
    setTimeout(() => this.refresh(), delay);
  }
};

// Throttled and debounced versions of refresh
currencyManager.refreshThrottled = utils.throttle(currencyManager.refresh.bind(currencyManager), 0);
currencyManager.refreshDebounced = utils.debounce(currencyManager.refresh.bind(currencyManager), 0);

// Hide the text columns in Collections/Eras mega menus via CSS so they are never
// rendered visibly on first open — avoids any layout shift before JS can run.
(function() {
  const style = document.createElement('style');
  style.textContent = [
    '@media (min-width: 990px) {',
    '  [data-header-float-element-template="HeaderDropdownMenu-collections"] .mega-menu__columns,',
    '  [data-header-float-element-template="HeaderDropdownMenu-eras"] .mega-menu__columns {',
    '    display: none;',
    '  }',
    '}'
  ].join('\n');
  document.head.appendChild(style);
})();

document.addEventListener('DOMContentLoaded', function() {
  const domCache = {
    body: document.body,
    cartDrawer: document.querySelector('#CartDrawer'),
    quickView: document.querySelector('#Quick-view'),
    cartPage: document.querySelector('#CartPage'),
    productsGrid: document.getElementById('ShopProductsGrid'),
    searchSidebar: document.querySelector('#SearchSidebar'),
    announcementBar: document.querySelector('.announcement-bar__rte')
  };

  // theme amendment - every other currencyManager.refresh() call site is reactive (cart/quickview/
  // variant/mutation events), so without this, .money elements rendered on the initial page load
  // never get converted from GBP into the customer's local currency until something else happens
  // to trigger a refresh. Retries internally (see currencyManager.refresh) until
  // window.themeCurrencyConverter is defined, so load order relative to currency-converter.js doesn't matter.
  currencyManager.refresh();
  // end theme amendment

    // Update existing sale badges for wholesale users on page load
  const isWholesaleCustomer = typeof window.customerTags !== 'undefined' && 
                             (window.customerTags.includes('wholesale') || 
                             JSON.parse(window.customerTags.replace(/&quot;/g, '"')).includes('wholesale'));
  const isWholesaleExport = typeof window.customerTags !== 'undefined' && 
                            (() => {
                              let tags = [];
                              if (Array.isArray(window.customerTags)) {
                                tags = window.customerTags;
                              } else if (typeof window.customerTags === 'string') {
                                try {
                                  tags = JSON.parse(window.customerTags.replace(/&quot;/g, '"'));
                                } catch (e) {
                                  tags = [window.customerTags];
                                }
                              }
                              const hasWholesale = tags.includes('wholesale');
                              const hasEurope = tags.includes('europe');
                              const hasUsa = tags.includes('usa');
                              return hasWholesale && (hasEurope || hasUsa);
                            })();

  if (isWholesaleCustomer) {
    document.querySelectorAll('.product-sale-amount-badge').forEach(badge => {
      badge.textContent = 'SALE';
    });
  }
  
  // Create a function to efficiently get and cache DOM elements
  const getElement = (selector, parent = document) => {
    const cacheKey = `${parent === document ? 'doc' : 'elem'}_${selector}`;
    if (!domCache[cacheKey]) {
      domCache[cacheKey] = parent.querySelector(selector);
    }
    return domCache[cacheKey];
  };
  
  const getAllElements = (selector, parent = document) => {
    return parent.querySelectorAll(selector);
  };
  
  // Observer registry to track and clean up observers
  const observerRegistry = {
    observers: [],
    
    create(element, config, handlerFunctions) {
      if (!element) return null;
      
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          handlerFunctions.forEach(handler => handler(mutation, element));
        });
      });
      
      observer.observe(element, config);
      
      // Store observer reference for potential cleanup
      this.observers.push({
        observer,
        element,
        disconnect: () => {
          observer.disconnect();
          this.observers = this.observers.filter(o => o.observer !== observer);
        }
      });
      
      return observer;
    },
    
    // Clean up observers for removed elements
    cleanupObservers() {
      this.observers = this.observers.filter(entry => {
        if (!document.body.contains(entry.element)) {
          entry.observer.disconnect();
          return false;
        }
        return true;
      });
    }
  };
  
  // Set up periodic observer cleanup
  setInterval(() => observerRegistry.cleanupObservers(), 30000);
  
  // Handler Functions
// theme amendment - build the free-shipping message per element so US customers (duty-inclusive
// prices), EU customers (VAT-inclusive prices), and everyone else (real prices, unchanged) can be
// computed differently. For US customers, the "amount away" figure uses the merchant's worst-case
// duty rate (data-us-duty-multiplier) against the real goal (data-threshold) - spending at least
// that much in duty-inclusive prices is guaranteed to reach the real goal, however it's split
// across products, since it assumes the highest duty rate in the catalog. For EU customers, VAT is
// a single flat rate per country rather than per-product, so the exact multiplier
// (data-eu-vat-multiplier) is used instead of a worst case.
function buildFreeShippingMessageHTML(titleEl, cart) {
  const wrapper = titleEl.closest('.free-shipping-bar');
  const isUsCustomer = wrapper && wrapper.dataset.isUs === 'true';
  const isEuCustomer = wrapper && wrapper.dataset.isEu === 'true'; // theme amendment
  const cartTotalWithoutCurrency = cart.total_price; // pennies, real GBP - this store's cart only ever settles in GBP

  // theme amendment - data-threshold comes straight from the merchant-entered "Goal" setting
  // (e.g. 150, meaning £150), not pennies, so it must be converted to pennies before comparing
  // against cart.total_price. Previously this also multiplied by Shopify.currency.rate, on the
  // assumption cart.total_price could be in a non-GBP "active presentment currency" - but this
  // store enforces cart_currency=GBP on every cart regardless of the customer's localization
  // (confirmed via the cart_currency cookie), so cart.total_price is always plain GBP pennies.
  // Multiplying by Shopify.currency.rate (which can be non-1 for a Markets "display currency"
  // distinct from the enforced cart currency) silently corrupted this comparison specifically -
  // computeEuCartBreakdown() never applied any such rate to cart.total_price and was unaffected.
  const freeShippingThreshold = (parseFloat(wrapper && wrapper.dataset.threshold) || 150) * 100;
  // end theme amendment

  if (isUsCustomer) {
    const dutyMultiplier = parseFloat(wrapper.dataset.usDutyMultiplier) || 1;

    if (cartTotalWithoutCurrency >= freeShippingThreshold) {
      return `
      <div class='free-shipping-bar__title'>
        <span>Free shipping unlocked!</span>
      </div>`;
    }

    const dutyInclusiveDifference = Math.round((freeShippingThreshold - cartTotalWithoutCurrency) * dutyMultiplier);
    const formattedDifference = utils.formatMoney(dutyInclusiveDifference);
    return `
      <div class='free-shipping-bar__title'>
        <span>Only <span class="money">${formattedDifference}</span> away from free shipping!</span>
      </div>`;
  }

  // theme amendment - EU VAT-inclusive "amount away" message (exact, not worst-case)
  if (isEuCustomer) {
    const vatMultiplier = parseFloat(wrapper.dataset.euVatMultiplier) || 1;

    if (cartTotalWithoutCurrency >= freeShippingThreshold) {
      return `
      <div class='free-shipping-bar__title'>
        <span>Free shipping unlocked!</span>
      </div>`;
    }

    const vatInclusiveDifference = Math.round((freeShippingThreshold - cartTotalWithoutCurrency) * vatMultiplier);
    const formattedDifference = utils.formatMoney(vatInclusiveDifference);
    return `
      <div class='free-shipping-bar__title'>
        <span>Only <span class="money">${formattedDifference}</span> away from free shipping!</span>
      </div>`;
  }
  // end theme amendment

  if (cartTotalWithoutCurrency >= freeShippingThreshold) {
    return `
      <div class='free-shipping-bar__title'>
        <span>Free shipping unlocked!</span>
      </div>`;
  }

  const difference = freeShippingThreshold - cartTotalWithoutCurrency;
  const formattedDifference = utils.formatMoney(difference);
  return `
      <div class='free-shipping-bar__title'>
        <span>Only <span class="money">${formattedDifference}</span> away from free shipping!</span>
      </div>`;
}
// end theme amendment

// theme amendment - the base theme's native <free-shipping-bar> custom element only sets the
// visual bar-fill percentage once, in its connectedCallback() (i.e. on initial page load / first
// mount). It's never recalculated after that, so on any cart change that doesn't fully replace the
// section HTML, the fill visually freezes while the text above (updated live by this file) keeps
// changing - making the two look out of sync. This recalculates the fill the same way the native
// class does (same data-threshold, same Shopify.currency.rate conversion) so it moves in step with
// the text on every cart update, for every customer (not duty-specific - the native bug affects
// everyone, it's just far more noticeable for US customers since the duty-inclusive text now moves
// in bigger, less intuitive jumps against what looked like a stalled bar).
function updateFreeShippingBarFill(titleEl, cart) {
  const wrapper = titleEl.closest('.free-shipping-bar');
  if (!wrapper) return;

  const dots = wrapper.querySelector('[data-free-shipping-progress-bar-dots]');
  if (!dots) return;

  const threshold = parseFloat(wrapper.dataset.threshold) || 0;
  if (!threshold) return;

  // theme amendment - threshold is in the merchant's "Goal" setting units (e.g. 150 = £150), but
  // cart.total_price is in pennies, so it must be converted to pennies before dividing - otherwise
  // the fill percentage comes out ~100x too high. See buildFreeShippingMessageHTML()'s comment
  // above for why this no longer also multiplies by Shopify.currency.rate.
  const thresholdCents = threshold * 100;
  const percent = Math.max(0, Math.min(100, (cart.total_price / thresholdCents) * 100));
  // end theme amendment

  dots.setAttribute('style', `--gsc-freeshipping-bar-percent: ${percent}%`);
}
// end theme amendment

function updateFreeShippingMessage(cart) {
  // Get elements directly each time to ensure we have the latest DOM references
  const cartDrawerFreeShippingBar = document.querySelector('#CartDrawer .free-shipping-bar__title');
  const cartPageFreeShippingBar = document.querySelector('#CartPage .free-shipping-bar__title');

  if (!cartDrawerFreeShippingBar && !cartPageFreeShippingBar) {
    return;
  }

  // Update cart drawer if it exists
  if (cartDrawerFreeShippingBar) {
    updateFreeShippingBarFill(cartDrawerFreeShippingBar, cart); // theme amendment
    cartDrawerFreeShippingBar.outerHTML = buildFreeShippingMessageHTML(cartDrawerFreeShippingBar, cart);
  }

  // Update cart page if it exists
  if (cartPageFreeShippingBar) {
    updateFreeShippingBarFill(cartPageFreeShippingBar, cart); // theme amendment
    cartPageFreeShippingBar.outerHTML = buildFreeShippingMessageHTML(cartPageFreeShippingBar, cart);
  }
}

// theme amendment - EU cart Subtotal/Shipping/Duties/Taxes/Total breakdown (see
// snippets/cart-eu-totals-breakdown.liquid), matching the structure Shopify's own checkout shows
// once an EU address is entered: Subtotal/Shipping/Duties are the real ex-VAT GBP figures, VAT on
// all three combined is a single Taxes line, and only Total is VAT-inclusive and
// currency-converted. Liquid pre-renders every figure using
// settings.eu_fallback_eur_to_gbp_rate_pence so the cart is never blank on first paint; this redoes
// the exact same threshold/flat-fee/ad-valorem/shipping/VAT math with a live EUR->GBP rate from
// frankfurter.dev and patches just the numbers in place once it resolves. The ad-valorem duty
// amount itself is NOT recomputed here - the cart AJAX API doesn't expose product metafields, so
// Liquid already did that exact (FX-free, pure GBP) calculation server-side and passed it through
// as data-eu-duty-ad-valorem-gbp.
const EU_RATE_CACHE_KEY = 'themeEurGbpRate';
const EU_RATE_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

function getEurToGbpRate() {
  try {
    const cached = JSON.parse(localStorage.getItem(EU_RATE_CACHE_KEY));
    if (cached && typeof cached.rate === 'number' && Date.now() - cached.ts < EU_RATE_CACHE_TTL_MS) {
      return Promise.resolve(cached.rate);
    }
  } catch (e) {}

  return fetch('https://api.frankfurter.dev/v1/latest?base=EUR&symbols=GBP')
    .then(response => {
      if (!response.ok) throw new Error('frankfurter.dev response not ok');
      return response.json();
    })
    .then(data => {
      const rate = data && data.rates && data.rates.GBP;
      if (typeof rate !== 'number') throw new Error('missing GBP rate in response');
      try {
        localStorage.setItem(EU_RATE_CACHE_KEY, JSON.stringify({ rate, ts: Date.now() }));
      } catch (e) {}
      return rate;
    })
    .catch(() => (typeof window.themeEuFallbackRate === 'number' ? window.themeEuFallbackRate : 0.86));
}

// Writes a pennies amount into a headline row's money span (currency-converted by the currency
// converter afterwards) and its bracketed GBP reference span (always literal GBP, matching
// format-gbp-bracket's Liquid output). Used for both headline rows - Subtotal (VAT-inclusive,
// matches what the cart's line items already sum to) and Total. theme amendment - the .money span
// is ALWAYS written in symbol style ("£3.10"), never code style ("GBP 3.10"), regardless of
// settings.show_currency_code: this breakdown only ever renders for actual EU-country customers
// (never GB), so it will always be converted into the customer's local currency - and its
// original text parser only recognises the symbol-style format (confirmed: code-style text was
// silently left unconverted, while the existing free-shipping-bar message, which has used this
// exact symbol-style formatMoney call all along, converts correctly).
function writeEuConvertedRow(container, pennies) {
  if (!container) return;
  const moneyEl = container.querySelector('[data-eu-amount]');
  if (moneyEl) {
    const newText = utils.formatMoney(pennies);
    // theme amendment - only touch the DOM (and clear the fresh-GBP stash below) if the figure
    // actually changed. computeEuCartBreakdown() re-runs this on every cart update, including ones
    // where this particular total didn't move - writing (and mutating) unconditionally just adds
    // avoidable churn to a subtree multiple MutationObservers are already watching.
    if (moneyEl.textContent !== newText) {
      moneyEl.textContent = newText;
      // theme amendment - this element is reused across cart updates rather than re-rendered from
      // fresh Liquid, so the currency converter's stashed "original GBP" value must be cleared here
      // or it would keep reconverting from a stale total instead of the one just written above.
      if (window.themeCurrencyConverter) window.themeCurrencyConverter.markFreshGbp(moneyEl);
      // end theme amendment
    }
  }
  const bracketEl = container.querySelector('[data-eu-bracket]');
  if (bracketEl) {
    const newBracketText = `(${utils.formatMoney(pennies)})`;
    if (bracketEl.textContent !== newBracketText) {
      bracketEl.textContent = newBracketText;
    }
  }
}

// theme amendment - Subtotal/Shipping/Duties/Taxes are always shown as literal GBP (this store
// settles in GBP, so that's the real figure checkout will charge), never currency-converted - so
// this just writes plain text into a span with no .money class, which keeps the currency
// converter (which only rewrites .money-classed elements) from ever touching it.
function writeEuLiteralRow(wrapper, rowName, pennies, hideIfZero) {
  const row = wrapper.querySelector(`[data-eu-row="${rowName}"]`);
  if (!row) return;
  if (hideIfZero) {
    row.hidden = pennies === 0;
  }
  const amountEl = row.querySelector('[data-eu-literal-amount]');
  if (amountEl) {
    const newText = utils.formatMoney(pennies);
    if (amountEl.textContent !== newText) {
      amountEl.textContent = newText;
    }
  }
}

function computeEuCartBreakdown(cart) {
  ['#CartPage', '#CartDrawer'].forEach(rootSelector => {
    const wrapper = document.querySelector(`${rootSelector} [data-eu-cart-breakdown]`);
    if (!wrapper) return;

    // theme amendment - products VAT is FX-free (a flat rate against the real GBP line prices), so
    // it's computed once server-side and passed straight through, unlike duty which depends on the
    // live EUR->GBP rate to know whether the cart has crossed the EUR-denominated duty threshold
    const subtotalVatAmount = parseInt(wrapper.dataset.euSubtotalVatAmount, 10) || 0;
    const flatFeeEur = parseFloat(wrapper.dataset.euDutyFlatFeeEur) || 0;
    const thresholdEur = parseFloat(wrapper.dataset.euDutyThresholdEur) || 0;
    // theme amendment - UK-set rate, entered directly in GBP pennies, never EUR-converted
    const shippingStandardGbpPennies = parseInt(wrapper.dataset.euShippingStandardGbpPennies, 10) || 0;
    const adValoremGbp = parseInt(wrapper.dataset.euDutyAdValoremGbp, 10) || 0;
    const dutyEnabled = wrapper.dataset.euDutyEnabled === 'true';
    const vatMultiplier = parseFloat(wrapper.dataset.euVatMultiplier) || 1;
    const lineItemCount = parseInt(wrapper.dataset.euLineItemCount, 10) || 0;
    const freeShippingThreshold = parseFloat(wrapper.dataset.euFreeShippingThreshold) || 0;
    // theme amendment - a free-shipping discount code/automatic discount waives shipping regardless
    // of the merchandise threshold below - see has_free_shipping_discount in sections/cart.liquid /
    // cart-drawer.liquid, which detects target_type == 'shipping_line' on a cart-level discount
    const hasFreeShippingDiscount = wrapper.dataset.euHasFreeShippingDiscount === 'true';

    // theme amendment - both the ex-VAT subtotal (cart.total_price) and the VAT-inclusive headline
    // subtotal (that figure plus the FX-free products VAT) need no FX and no server round-trip, so
    // both are written immediately rather than waiting on the frankfurter fetch
    const subtotalExVat = cart.total_price;
    const subtotalIncVat = subtotalExVat + subtotalVatAmount;
    writeEuLiteralRow(wrapper, 'subtotal-ex-vat', subtotalExVat, false);
    writeEuConvertedRow(wrapper.querySelector('[data-eu-row="subtotal"]'), subtotalIncVat);

    getEurToGbpRate().then(rate => {
      const thresholdGbpPennies = Math.round(thresholdEur * rate * 100);
      const flatFeeGbpPennies = Math.round(flatFeeEur * rate * 100);

      let dutyExVat = 0;
      if (cart.total_price > thresholdGbpPennies) {
        if (dutyEnabled) dutyExVat = adValoremGbp;
      } else {
        dutyExVat = flatFeeGbpPennies * lineItemCount;
      }

      const isFreeShipping = cart.total_price >= freeShippingThreshold * 100 || hasFreeShippingDiscount;
      const shippingExVat = isFreeShipping ? 0 : shippingStandardGbpPennies;

      const dutyVatAmount = Math.round(dutyExVat * vatMultiplier) - dutyExVat;
      const shippingVatAmount = Math.round(shippingExVat * vatMultiplier) - shippingExVat;
      const taxesAmount = subtotalVatAmount + dutyVatAmount + shippingVatAmount;
      const grandTotal = subtotalExVat + dutyExVat + shippingExVat + taxesAmount;
      // theme amendment - VAT-inclusive counterparts for the two headline rows below, so
      // Subtotal(inc-Tax) + Shipping(inc-Tax) + Duties(inc-Tax) sums to Total exactly, matching
      // sections/cart.liquid/cart-drawer.liquid's eu_shipping_total_vat_inclusive /
      // eu_duty_total_vat_inclusive server-side figures used on first paint
      const dutyIncVat = dutyExVat + dutyVatAmount;
      const shippingIncVat = shippingExVat + shippingVatAmount;

      writeEuLiteralRow(wrapper, 'duty', dutyExVat, true);
      writeEuLiteralRow(wrapper, 'taxes', taxesAmount, true);

      const shippingRow = wrapper.querySelector('[data-eu-row="shipping"]');
      if (shippingRow) {
        const priceEl = shippingRow.querySelector('[data-eu-shipping-price]');
        if (priceEl) {
          if (shippingExVat === 0) {
            priceEl.innerHTML = '<span data-eu-free-label>Free</span>';
          } else {
            priceEl.innerHTML = `<span data-eu-literal-amount>${utils.formatMoney(shippingExVat)}</span>`;
          }
        }
      }

      // theme amendment - headline-sized Shipping row (shipping_headline_row in
      // cart-eu-totals-breakdown.liquid), wrapped in .money so the currency converter converts it
      // - unlike shippingRow above, which stays literal GBP by design. Shows shippingIncVat (VAT-inclusive), not the
      // ex-VAT shippingExVat figure shippingRow above uses, so Subtotal + Shipping + Duties sums to
      // Total. The Free-label check still keys off shippingExVat, the one true "is shipping free"
      // signal (0 x any multiplier is still 0).
      const shippingHeadlineRow = wrapper.querySelector('[data-eu-row="shipping-headline"]');
      if (shippingHeadlineRow) {
        const headlinePriceEl = shippingHeadlineRow.querySelector('[data-eu-shipping-headline-price]');
        if (headlinePriceEl) {
          if (shippingExVat === 0) {
            headlinePriceEl.innerHTML = '<span data-eu-free-label>Free</span>';
          } else {
            headlinePriceEl.innerHTML = `<span class="money" data-eu-amount>${utils.formatMoney(shippingIncVat)}</span>`;
          }
        }
      }
      // end theme amendment

      // theme amendment - headline-sized Duties row (duty_headline_row in
      // cart-eu-totals-breakdown.liquid), sitting under shippingHeadlineRow above. Shows dutyIncVat
      // (VAT-inclusive), not the ex-VAT dutyExVat figure written into the literal 'duty' row above -
      // same reasoning as shippingHeadlineRow. Hidden whenever there's no duty to charge, keyed off
      // dutyExVat (the one true "is there any duty" signal), same condition writeEuLiteralRow used
      // for the literal 'duty' row.
      const dutyHeadlineRow = wrapper.querySelector('[data-eu-row="duty-headline"]');
      if (dutyHeadlineRow) {
        dutyHeadlineRow.hidden = dutyExVat === 0;
        writeEuConvertedRow(dutyHeadlineRow, dutyIncVat);
      }
      // end theme amendment

      const totalRow = wrapper.querySelector('[data-eu-row="total"]');
      writeEuConvertedRow(totalRow, grandTotal);

      currencyManager.refreshDebounced();
    });
  });
}

// Recalculates --gsc-eu-duty-panel-max-height (half the items-viewport height) so the drawer's EU
// breakdown accordion can't expand far enough to push the checkout button off-screen - see the
// max-height cap in assets/override.css. This is purely cosmetic now (the <details> element hides
// its own content correctly regardless), so a wrong/small value just means the open panel scrolls
// sooner than ideal, not a broken layout - but called from a mutation handler that can fire mid-open
// transition, before the drawer has reached its final size, so: (a) run after a rAF so layout has
// settled, and (b) ignore obviously-too-small readings (mid-transition) rather than writing a tiny
// cap that would make the expanded panel nearly unusable
function updateEuDutyPanelMaxHeight() {
  requestAnimationFrame(() => {
    const viewport = document.querySelector('#CartDrawer [data-cart-drawer-viewport]');
    const breakdown = document.querySelector('#CartDrawer [data-eu-cart-breakdown]');
    if (!viewport || !breakdown) return;
    const maxHeight = Math.round(viewport.clientHeight / 2);
    if (maxHeight < 100) return;

    // theme amendment - this fires from handleCartDrawerMutations() on EVERY mutation inside the
    // drawer (it's the cheapest reliable "did anything change" signal available), and writing the
    // custom property below is itself an attribute mutation on an element inside that same
    // observed subtree. Without this guard, every call re-writes the same value, which re-triggers
    // the observer, which calls this again - an infinite mutation feedback loop that pegs the main
    // thread (reproduced as the tab hanging once the EU cart breakdown panel is present, e.g. on
    // drawer close). Skipping the write when the value hasn't actually changed breaks the loop.
    const newValue = `${maxHeight}px`;
    if (breakdown.style.getPropertyValue('--gsc-eu-duty-panel-max-height') === newValue) return;
    breakdown.style.setProperty('--gsc-eu-duty-panel-max-height', newValue);
  });
}
// end theme amendment

// Add this to the DOMContentLoaded event near other cart page handlers
if (domCache.cartPage) {
  
  // Direct handler for cart remove buttons 
  document.addEventListener('click', function(e) {
    if (e.target.closest('cart-remove-button')) {
      // Wait for the cart AJAX to complete
      setTimeout(() => {
        cartManager.fetchCartData().then(updateFreeShippingMessage);
      }, 1000);
    }
  });

  // Force initial update
  setTimeout(function() {
    cartManager.fetchCartData().then(updateFreeShippingMessage);
  }, 500);
  
  if (isWholesaleExport) {
    const updateCartPageButton = () => {
      const cartPageButton = domCache.cartPage.querySelector('button[data-cart-page-checkout-button] span[data-button-text]');
      if (cartPageButton && cartPageButton.textContent !== 'Complete Order') {
        cartPageButton.textContent = 'Complete Order';
        
        const pageBtn = domCache.cartPage.querySelector('button[data-cart-page-checkout-button]');
        if (pageBtn) {
          pageBtn.setAttribute('aria-label', 'Complete Order');
        }
      }
    };
    
    // Initial update
    updateCartPageButton();
    
    // The existing cart page observer will catch any subsequent changes
  }
}
  
  function updateExVatPrice() {
    const priceElements = getAllElements('.ymq-b2b-price-span');
    
    // Helper function to fix floating-point rounding in RRP prices
    // Checks if price is within 0.02 of a whole number and rounds if so
    function fixRrpRounding(priceValue) {
      const rounded = Math.round(priceValue * 100) / 100;
      const nearestWhole = Math.round(rounded);
      const diff = Math.abs(rounded - nearestWhole);
      
      if (diff <= 0.02) {
        // Very close to a whole number, use the whole number
        return nearestWhole.toFixed(2);
      } else {
        // Not close to whole, keep the rounded value
        return rounded.toFixed(2);
      }
    }
    
    // Helper function to remove US 5% markup from RRP
    // US prices have 5% added, so we need to divide by 1.05 to get the true RRP
    function removeUsMarkup(priceValue) {
      return priceValue / 1.05;
    }
    
    // Check if customer is a US customer (has 'usa' tag)
    let isUsCustomer = false;
    if (typeof window.customerTags !== 'undefined') {
      let tags = [];
      if (Array.isArray(window.customerTags)) {
        tags = window.customerTags;
      } else if (typeof window.customerTags === 'string') {
        try {
          tags = JSON.parse(window.customerTags.replace(/&quot;/g, '"'));
        } catch (e) {
          // If parsing fails, treat as single tag
          tags = [window.customerTags];
        }
      }
      isUsCustomer = tags.includes('usa') || tags.includes('europe');
    }

    priceElements.forEach((priceElement) => {
      const price = parseFloat(priceElement.getAttribute('data-ymq-money'));
      if (!isNaN(price)) {
        const exVatPrice = (price / 1.2).toFixed(2);
        
        // Check if this price is in cart totals section (these should show original price)
        const isCartTotalPrice = priceElement.closest('.cart-subtotal__totals-price') || 
                                 priceElement.closest('.cart-drawer-subtotal__price');
        
        const isProductPageOrQuickView = priceElement.closest('.product-price__content');
        
        // Get the retail price from the product's own price container
        let retailPrice = '';
        if (isProductPageOrQuickView) {
          const priceContainer = priceElement.closest('.product-price__content');
          if (priceContainer) {
            const localRrpElement = priceContainer.querySelector('#get-rrp');
            if (localRrpElement) {
              // Get the retail price with potential money spans
              const rawRetailPrice = localRrpElement.getAttribute('data-retail-price');
              
              // Create a temporary div to parse the HTML and extract just the text
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = rawRetailPrice;
              // Get only the text content from the parsed HTML (removes all HTML tags)
              retailPrice = (tempDiv.textContent || tempDiv.innerText || '').trim();
              
              // Fix floating-point rounding issues in RRP
              // Extract the numeric value, apply proper rounding, and reformat
              const rrpMatch = retailPrice.match(/£?([\d,]+\.?\d*)/);
              if (rrpMatch) {
                let rrpValue = parseFloat(rrpMatch[1].replace(',', ''));
                
                // Remove 5% US markup if customer has 'usa' tag
                if (isUsCustomer) {
                  rrpValue = removeUsMarkup(rrpValue);
                }
                
                const fixedRrp = fixRrpRounding(rrpValue);
                retailPrice = '£' + fixedRrp;
              }
            }
          }
        }
        
        if (typeof customerCountryCode !== 'undefined' && customerCountryCode === 'GB') {
          // Create ex-VAT price element
          let exVatElement = priceElement.previousElementSibling;
          if (!exVatElement || !exVatElement.classList.contains('ex-vat-price')) {
            exVatElement = document.createElement('span');
            exVatElement.classList.add('ex-vat-price');
            exVatElement.style.display = 'block';
            priceElement.insertAdjacentElement('beforebegin', exVatElement);
          }
          
          // Add RRP after ex-VAT price if on product page or quick view
          if (isProductPageOrQuickView) {
            exVatElement.innerHTML = `£${exVatPrice} <span class="retail-price">(RRP ${retailPrice})</span>`;
          } else {
            exVatElement.textContent = `£${exVatPrice}`;
          }
          
          // Add inc-VAT text to the original price element
          const originalText = priceElement.textContent.trim();
          if (!originalText.includes('inc-VAT')) {
            priceElement.textContent = `${originalText.replace(/\s*\(RRP.*\)/, '').trim()} inc-VAT`;
          }
          
          // Only display original price in cart totals, hide it elsewhere
          priceElement.style.display = isCartTotalPrice ? '' : 'none';
        } else {
          // For non-UK customers
          priceElement.style.display = ''; // Always show original price
          
          // Add RRP directly to the price element if on product page/quick view
          if (isProductPageOrQuickView && retailPrice) {
            let retailPriceSpan = priceElement.querySelector('.retail-price');
            if (!retailPriceSpan) {
              retailPriceSpan = document.createElement('span');
              retailPriceSpan.classList.add('retail-price');
              priceElement.appendChild(document.createTextNode(' '));
              priceElement.appendChild(retailPriceSpan);
            }
            retailPriceSpan.innerHTML = `(RRP ${retailPrice})`;
          }
          
          // Remove any existing ex-VAT elements for non-UK customers
          const exVatElement = priceElement.previousElementSibling;
          if (exVatElement && exVatElement.classList.contains('ex-vat-price')) {
            exVatElement.remove();
          }
        }
      }
    });
  }
  
function handleYmqPrice(ymqCartTotalSpan) {
  try {
    // Check if the span exists
    if (!ymqCartTotalSpan) return;
    
    var ymqMoneySpan = ymqCartTotalSpan.querySelector('[ymq-b2b-money]');
    
    if (ymqMoneySpan) {
      var ymqPriceTotalString = ymqMoneySpan.getAttribute('ymq-b2b-money');
      var ymqPriceTotal = parseFloat(ymqPriceTotalString);
      
      // Safely access customer tags
      var customerTagsRaw = window.customerTags;
      if (!customerTagsRaw) return;
      
      var customerTags;
      try {
        // Handle different possible formats of customerTags
        if (typeof customerTagsRaw === 'string') {
          customerTags = JSON.parse(customerTagsRaw.replace(/&quot;/g, '"'));
        } else if (Array.isArray(customerTagsRaw)) {
          customerTags = customerTagsRaw;
        } else {
          return; // Invalid format, exit safely
        }
      } catch (e) {
        console.error("Error parsing customer tags:", e);
        return; // Exit the function rather than crashing
      }
      
      if (customerTags && customerTags.includes('wholesale')) {
        // If customer is from UK, use ex-VAT price for minimum order check
        var priceToCheck = ymqPriceTotal;
        if (typeof customerCountryCode !== 'undefined' && customerCountryCode === 'GB') {
          // Remove 20% VAT for UK customers
          priceToCheck = ymqPriceTotal / 1.2;
        }
        
        // Check against minimum order threshold using appropriate price
        if (priceToCheck < 300) {
          if (!document.querySelector('.caption.minimum-order-value')) {
            var actionDiv = document.createElement('div');
            actionDiv.className = 'caption minimum-order-value';  // Fixed class name
            actionDiv.style.color = 'red';
            actionDiv.style.textAlign = 'left';
            actionDiv.innerHTML = 'Minimum order value £300';
            
            // Insert after the total price and before the taxes line
            // Try cart page first, then cart drawer
            var taxesDiv = document.querySelector('.cart-subtotal__taxes');
            if (!taxesDiv) {
              taxesDiv = document.querySelector('.cart-drawer-subtotal__tax-note');
            }
            
            if (taxesDiv && taxesDiv.parentNode) {
              taxesDiv.parentNode.insertBefore(actionDiv, taxesDiv);
            }
            
            // Disable checkout button (cart page)
            var checkoutButton = document.getElementById('checkout');
            if (checkoutButton) {
              checkoutButton.disabled = true;
            }
            
            // Disable checkout button (cart drawer)
            var drawerCheckoutButton = document.querySelector('[data-cart-checkout-button]');
            if (drawerCheckoutButton) {
              drawerCheckoutButton.disabled = true;
            }
          }
        }
      }
    }
  } catch (error) {
    // Silently handle error
  }
}
  
  function hideYmqAttribution() {
    getAllElements('.ymq-b2b-app-powered-by').forEach(link => {
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    });
  }
  
function hideYmqLockPasscode() {
  // Target both classes for better coverage
  const selectors = [
    '.ymq-lock-request-passcode', 
    '.ymq-lock-input-bottom-text',
    '.ymq-lock-input-bottom-text.ymq-lock-request-passcode'
  ];
  
  selectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      if (element && element.style) {
        // Apply multiple CSS properties to ensure hiding works
        element.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; overflow: hidden !important; pointer-events: none !important;';
      }
    });
  });
}

// theme amendment - apply the duty/VAT-inclusive multiplier (window.themeDutyVatMultiplier, set
// globally in snippets/customjs.liquid) to any .money element also marked
// .free-shipping-threshold, so a free-shipping figure quoted anywhere on the site (e.g. the
// announcement bar) matches the duty/VAT-inclusive prices shown on product pages, the same way
// the in-cart free-shipping-bar's "amount away" message already does. Guarded by
// data-duty-vat-applied so it only ever runs once per element no matter how many times the
// currency converter re-renders its text - multiplication order doesn't affect correctness
// (raw x multiplier x FX-rate == raw x FX-rate x multiplier), only that it happens exactly once.
// NOTE: the currency converter (assets/currency-converter.js) explicitly clears this guard
// whenever it rewrites a .free-shipping-threshold element, so the multiplier is correctly
// re-applied on every reconversion rather than being permanently skipped after the first one.
function applyDutyVatMultiplierToThreshold(element) {
  if (!element.classList.contains('free-shipping-threshold') || element.dataset.dutyVatApplied) {
    return;
  }
  const multiplier = window.themeDutyVatMultiplier;
  if (!multiplier || multiplier === 1) {
    element.dataset.dutyVatApplied = 'true';
    return;
  }

  const text = element.textContent.trim();
  const match = text.match(/^(\D*)([\d.,]+)(\D*)$/);
  if (!match) return;

  const [, prefix, numberPart, suffix] = match;
  const lastSeparatorIndex = Math.max(numberPart.lastIndexOf('.'), numberPart.lastIndexOf(','));
  let normalized = numberPart;
  if (lastSeparatorIndex !== -1) {
    const intPart = numberPart.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
    const decPart = numberPart.slice(lastSeparatorIndex + 1);
    normalized = `${intPart}.${decPart}`;
  }
  const value = parseFloat(normalized);
  if (isNaN(value)) return;

  element.textContent = `${prefix}${Math.round(value * multiplier)}${suffix}`;
  element.dataset.dutyVatApplied = 'true';
}
// end theme amendment

function formatAnnouncementBarPrices(mutation) {
    const formatPrice = (element) => {
      applyDutyVatMultiplierToThreshold(element); // theme amendment
      const price = element.textContent.trim();
      const match = price.match(/^([^\d]*\d+)\.\d{2}$/);

      if (match) {
        element.textContent = match[1];
      }
    };

    if (mutation.target.classList?.contains('money')) {
      formatPrice(mutation.target);
    }

    const newPriceElements = mutation.target.querySelectorAll?.('.money');
    if (newPriceElements) {
      newPriceElements.forEach(formatPrice);
    }
  }
  
  // Consolidated Event Handlers
  function handleCartOrQuickviewOpenForCurrency(mutation, element) {
  // Skip processing if the mutation is related to the carousel, checkmark animation, or any part of the free shipping bar
  if (mutation.attributeName === 'class' && 
      (mutation.target.classList.contains('card-carousel__viewport') ||
       mutation.target.classList.contains('embla') ||
       mutation.target.closest('.cart-drawer__on-side-related-products-carousel'))) {
    return;
  }
  
  // Skip if mutation involves checkmark animation
  if (mutation.target.classList.contains('checkmark-with-chevron') ||
      mutation.target.classList.contains('cart-item__checkmark-with-chevron') ||
      mutation.target.closest('[data-cart-item-checkmark]')) {
    return;
  }
  
  // Skip any mutations involving the free shipping bar or its children
  if (mutation.target.classList.contains('free-shipping-bar') ||
      mutation.target.classList.contains('free-shipping-bar__title') ||
      mutation.target.classList.contains('free-shipping-bar__progress') ||
      mutation.target.classList.contains('free-shipping-bar__progress-bar') ||
      mutation.target.classList.contains('free-shipping-bar__progress-bar-dots') ||
      mutation.target.closest('free-shipping-bar') ||
      mutation.target.hasAttribute('data-free-shipping-difference-percent') ||
      mutation.target.hasAttribute('data-free-shipping-progress-bar-dots')) {
    return;
  }
  
  // Skip if mutation happens inside free shipping bar
  if (mutation.target.closest('.free-shipping-bar')) {
    return;
  }
    
    if (mutation.attributeName === 'class' && 
        element.classList.contains('is-opened') &&
        !mutation.target.classList.contains('header-shadow-visible') &&
        !mutation.target.classList.contains('footer-shadow-visible')) {
      
      currencyManager.refreshWithDelay();
      
      if (element.id === 'Quick-view') {
        setTimeout(observeVariantChanges, 0);
      }
      
      if (element.id === 'CartDrawer') {
        handleCartDrawerOpen();
      }
    } else if (mutation.attributeName === 'class' &&
               element.id === 'CartDrawer' &&
               !element.classList.contains('is-opened')) {
      handleCartDrawerClose();
    }
  }
  
  function updateChatWidgetVisibility() {
    const cartDrawerOpen = domCache.cartDrawer && domCache.cartDrawer.classList.contains('is-opened');
    const searchSidebarOpen = domCache.searchSidebar && domCache.searchSidebar.classList.contains('is-opened');
    const stickyBuyButton = document.querySelector('.product-form__sticky-buy-button');
    const stickyBuyVisible = stickyBuyButton && stickyBuyButton.classList.contains('is-visible');
    // theme amendment - Shopify's chat widget moved from a plain element with id="shopify-chat"
    // to a custom element <shopify-chat> (with its own shadow root) and no id, so the old
    // id selector silently stopped matching anything. The tag-name selector below targets the
    // custom element host itself - setting visibility on the host still hides its shadow content.
    const chat = document.querySelector('shopify-chat');
    if (chat) chat.style.visibility = (cartDrawerOpen || searchSidebarOpen || stickyBuyVisible) ? 'hidden' : '';
  }

  function handleCartDrawerOpen() {
    cartManager.fetchCartData().catch(() => {});
    updateChatWidgetVisibility();
  }

  function handleCartDrawerClose() {
    updateChatWidgetVisibility();
  }
  
  function observeVariantChanges() {
    const quickViewContent = getElement('#Quick-view .product-variant-picker__pill-list');
    if (quickViewContent && !quickViewContent.dataset.variantObserverAttached) {
      quickViewContent.dataset.variantObserverAttached = true;
      
      observerRegistry.create(
        quickViewContent, 
        { attributes: true, attributeFilter: ['class'], subtree: true, childList: false },
        [() => currencyManager.refreshWithDelay()]
      );
    }
  }
  
  // Unified Mutation Handlers
  function handleBodyMutations(mutation) {
    // Handle YMQ branding removal
    const hasYmqLink = Array.from(mutation.addedNodes || []).some(node => {
      return node.classList?.contains('ymq-b2b-app-powered-by') ||
            (node.querySelectorAll && node.querySelectorAll('.ymq-b2b-app-powered-by').length > 0);
    });
    
    if (hasYmqLink) {
      hideYmqAttribution();
    }

    // Check for YMQ lock passcode classes - check for both classes
    const hasYmqLockPasscode = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      return node.classList?.contains('ymq-lock-request-passcode') ||
             node.classList?.contains('ymq-lock-input-bottom-text') ||
             (node.querySelectorAll && (
               node.querySelectorAll('.ymq-lock-request-passcode').length > 0 || 
               node.querySelectorAll('.ymq-lock-input-bottom-text').length > 0
             ));
    });
    
    if (hasYmqLockPasscode) {
      hideYmqLockPasscode();
    }
    // Check if customer is wholesale and update sale badges
    if (isWholesaleCustomer) {
      // Look for sale badges in added nodes
      if (mutation.addedNodes && mutation.addedNodes.length > 0) {
        Array.from(mutation.addedNodes).forEach(node => {
          if (node.nodeType === 1) { // Only process Element nodes
            // Check in the node itself
            if (node.classList && node.classList.contains('product-sale-amount-badge')) {
              node.textContent = 'SALE';
            }
            
            // Check in child nodes
            const saleBadges = node.querySelectorAll ? 
                               node.querySelectorAll('.product-sale-amount-badge') : [];
            saleBadges.forEach(badge => {
              badge.textContent = 'SALE';
            });
          }
        });
      }
    }
    
    // Handle quickview form submissions
    const quickviewForm = getElement('[id^="product-form-"][id*="---QuickView"]');
    if (quickviewForm && !quickviewForm.dataset.listenerAttached) {
      quickviewForm.addEventListener('submit', function() {
        setTimeout(() => {
          cartManager.fetchCartData()
            .then(cart => {
              // Update free shipping message
              updateFreeShippingMessage(cart);
              
              // Force YMQ price update for cart page by direct DOM query
              const ymqCartTotalSpan = document.querySelector('#CartPage [ymq-b2b-cart-total-price]');
              if (ymqCartTotalSpan) {
                // Remove any existing minimum order message before recalculating
                const existingMsg = document.querySelector('.minimum-order-value');
                if (existingMsg) {
                  existingMsg.remove();
                }
                
                // Re-evaluate minimum order value
                handleYmqPrice(ymqCartTotalSpan);
              }
              
              // Force immediate currency refresh
              currencyManager.refresh();
            })
            .catch(() => {});
        }, 3000);
      });
      quickviewForm.dataset.listenerAttached = true;
    }
    
    // Only update ex-VAT prices if price elements were potentially added/changed
    const hasPriceChanges = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      return node.classList?.contains('ymq-b2b-price-span') ||
             node.querySelector?.('.ymq-b2b-price-span');
    });
    
    if (hasPriceChanges) {
      updateExVatPrice();
    }
    
    // Track new menu links for Google Analytics (integrated with existing observer)
    const hasNewMenuLinks = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      const isMenuRelated = node.closest?.('.header') || 
                           node.closest?.('#DrawerMenu') || 
                           node.id === 'DrawerMenu' || 
                           node.classList?.contains('header') ||
                           node.classList?.contains('header__dropdown') ||
                           node.classList?.contains('help-links') ||
                           node.classList?.contains('footer');
      return isMenuRelated && node.querySelector?.('a[href]');
    });
    
    if (hasNewMenuLinks) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          const newLinks = node.querySelectorAll ? node.querySelectorAll('a[href]') : [];
          newLinks.forEach(link => {
            // Determine the location based on parent container
            let location = 'header_desktop';
            if (link.closest('#DrawerMenu')) {
              location = 'mobile_drawer';
            } else if (link.closest('.help-links')) {
              location = 'announcement_bar_help';
            } else if (link.closest('.footer')) {
              location = 'footer';
            }
            
            const isInMenu = link.closest('.header') || link.closest('#DrawerMenu') || link.closest('.help-links') || link.closest('.footer');
            if (isInMenu) {
              menuTracking.trackLink(link, location);
            }
          });
        }
      });
    }

    // Handle currency refresh when product-related-block receives its AJAX content.
    // mutation.target is the product-related-block element when replaceNodeChildren fires.
    if (mutation.target.tagName === 'PRODUCT-RELATED-BLOCK' && mutation.addedNodes.length > 0) {
      currencyManager.refresh();
      currencyManager.refreshWithDelay(400);
    }
    
    // Check for collection card titles in MENUS ONLY and remove "Vintage"
    const hasMenuCollectionCards = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      // Only check if it's within a menu container
      const isInMenu = node.closest?.('[data-header-float-element-template]') || 
                      node.closest?.('#DrawerMenu') ||
                      node.id === 'DrawerMenu' ||
                      (node.hasAttribute?.('data-header-float-element-template')) ||
                      (node.querySelector && (node.querySelector('[data-header-float-element-template]') || node.querySelector('#DrawerMenu')));
      if (!isInMenu) return false;
      return node.classList?.contains('collection-card__title') ||
             node.querySelector?.('.collection-card__title');
    });
    
    if (hasMenuCollectionCards) {
      removeVintageFromTitles();
    }
  }
  
  // theme amendment - when a discount code is applied/removed, the base theme's
  // CartDiscountCode component replaces the whole cart-drawer/cart-page section HTML, which
  // mounts a brand new <free-shipping-bar> custom element. Its native connectedCallback() runs
  // immediately and writes its own text using the shop's raw money format (always the store's
  // base currency, e.g. GBP) as plain text - not wrapped in a .money span - so the currency
  // converter (which only rewrites .money-classed elements) can never correct it. Previously
  // this only got overwritten with the money-span-wrapped, currency-convertible, duty-aware
  // version by buildFreeShippingMessageHTML() on specific triggers (initial load, remove-item
  // click, quantity form submit) - discount code changes weren't one of them, so the bar was
  // left showing a raw, unconverted GBP figure after a discount was applied/removed.
  function hasNewFreeShippingBar(mutation) {
    return Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      return node.classList?.contains('free-shipping-bar') ||
             node.querySelector?.('.free-shipping-bar');
    });
  }
  // end theme amendment

  function handleCartDrawerMutations(mutation, element) {

    // theme amendment - cheap no-op if the EU breakdown isn't present (non-EU customer, or
    // wholesale); recalculated on every drawer mutation since that's already the cheapest reliable
    // signal available for "the drawer just opened or its content changed size", without needing a
    // dedicated open-detector. IMPORTANT: updateEuDutyPanelMaxHeight() itself writes a style
    // attribute on [data-eu-cart-breakdown], which sits inside this same observed subtree
    // (attributes: true, subtree: true on #CartDrawer) - calling it for a mutation that IS that
    // exact style write re-triggers this same handler, which calls it again, forever. The
    // maxHeight-unchanged guard inside updateEuDutyPanelMaxHeight() isn't sufficient on its own -
    // clientHeight can jitter by a pixel between frames (e.g. a scrollbar toggling in/out as the
    // max-height itself changes what needs to scroll), so two slightly different values can keep
    // bouncing back and forth indefinitely. Filtering the causal mutation out here breaks the loop
    // structurally regardless of value jitter, rather than relying on the values coinciding exactly.
    const isOwnMaxHeightWrite = mutation.type === 'attributes' &&
      mutation.attributeName === 'style' &&
      mutation.target.hasAttribute && mutation.target.hasAttribute('data-eu-cart-breakdown');
    if (!isOwnMaxHeightWrite) {
      updateEuDutyPanelMaxHeight();
    }
    // end theme amendment

    // Only trigger currency refresh for relevant changes
    const relevantChange = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      return node.classList?.contains('money') ||
             node.querySelector?.('.money');
    });

    if (relevantChange) {
      currencyManager.refreshDebounced();
    }

    // theme amendment - re-run the duty-aware, currency-convertible free shipping message
    // whenever a fresh <free-shipping-bar> is mounted (see hasNewFreeShippingBar comment above)
    if (hasNewFreeShippingBar(mutation)) {
      cartManager.fetchCartData().then(updateFreeShippingMessage).catch(() => {});
    }
    // end theme amendment

    // Handle YMQ pricing for minimum order
    const ymqCartTotalSpan = element.querySelector('[ymq-b2b-cart-total-price]');
    if (ymqCartTotalSpan) {
      handleYmqPrice(ymqCartTotalSpan);
    }

    // Update checkout button text for wholesale export customers (Europe or USA)
    if (isWholesaleExport) {
      const cartDrawerButton = element.querySelector('button[data-cart-checkout-button] span[data-button-text]');
      if (cartDrawerButton && cartDrawerButton.textContent !== 'Complete Order') {
        cartDrawerButton.textContent = 'Complete Order';

        const drawerBtn = element.querySelector('button[data-cart-checkout-button]');
        if (drawerBtn) {
          drawerBtn.setAttribute('aria-label', 'Complete Order');
        }
      }
    }
  }

  function handleCartPageMutations(mutation, element) {
    // Only update currency for relevant changes
    const relevantChange = Array.from(mutation.addedNodes || []).some(node => {
      if (node.nodeType !== 1) return false;
      return node.classList?.contains('money') ||
             node.querySelector?.('.money');
    });

    if (relevantChange) {
      currencyManager.refreshDebounced();
    }

    // theme amendment - re-run the duty-aware, currency-convertible free shipping message
    // whenever a fresh <free-shipping-bar> is mounted (see hasNewFreeShippingBar comment above)
    if (hasNewFreeShippingBar(mutation)) {
      cartManager.fetchCartData().then(updateFreeShippingMessage).catch(() => {});
    }
    // end theme amendment

    // Handle YMQ pricing for minimum order
    const ymqCartTotalSpan = element.querySelector('[ymq-b2b-cart-total-price]');
    if (ymqCartTotalSpan) {
      handleYmqPrice(ymqCartTotalSpan);
    }
  }
  
  function handleProductsGridMutations(mutation) {
    const hasNewProducts = Array.from(mutation.addedNodes || []).some(node => 
      node.nodeType === 1 && 
      (node.classList?.contains('shop__grid-item') || 
       node.querySelector?.('.shop__grid-item'))
    );
    
    if (hasNewProducts) {
      setTimeout(() => currencyManager.refresh(), 0);
    }
  }
  
  function handleSearchSidebarMutations(mutation) {
    if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
      currencyManager.refreshDebounced();
    }
  }
  
  function handleSearchSidebarOpen(mutation, element) {
    if (mutation.attributeName === 'class') {
      updateChatWidgetVisibility();
      if (element.classList.contains('is-opened')) {
        const searchSidebarContent = getElement('.search-sidebar__content');
        if (searchSidebarContent && !searchSidebarContent.dataset.observerAttached) {
          searchSidebarContent.dataset.observerAttached = true;
          observerRegistry.create(
            searchSidebarContent, 
            { childList: true, subtree: true },
            [handleSearchSidebarMutations]
          );
        }
      }
    }
  }
  
  // Set up event delegation for cart interactions
  function setupCartEventDelegation() {
    // For cart drawer
    if (domCache.cartDrawer) {
      domCache.cartDrawer.addEventListener('click', function(e) {
        const quantityBtn = e.target.closest('quantity-btn.cart-item-quantity__button-wrapper');
        const removeBtn = e.target.closest('.cart-item__remove-button-icon');
        
        if (quantityBtn || removeBtn) {
         setTimeout(() => {
            cartManager.fetchCartData().then(updateFreeShippingMessage).catch(() => {});
          }, 2000);
        }
      });
    }
    
    // For cart page
    if (domCache.cartPage) {
      // Listen for form submission events on the cart page
      const cartForms = domCache.cartPage.querySelectorAll('form[action="/cart"]');
      cartForms.forEach(form => {
        form.addEventListener('submit', function() {
          setTimeout(() => {
            cartManager.fetchCartData().then(updateFreeShippingMessage);
          }, 0);
        });
      });
    }
  }
  
  // Register cart update handler
  cartManager.onCartUpdate(updateFreeShippingMessage);
  cartManager.onCartUpdate(computeEuCartBreakdown); // theme amendment

  // theme amendment - keep the drawer's EU breakdown max-height cap in sync with viewport resizes
  // (e.g. rotating a phone while the drawer is open)
  window.addEventListener('resize', utils.debounce(updateEuDutyPanelMaxHeight, 200));
  // end theme amendment

  // Set up consolidated mutation observers - only create what we need
  if (domCache.cartDrawer) {
    observerRegistry.create(
      domCache.cartDrawer,
      { attributes: true, childList: true, subtree: true },
      [handleCartOrQuickviewOpenForCurrency, handleCartDrawerMutations]
    );
  }
  
  if (domCache.quickView) {
    observerRegistry.create(
      domCache.quickView,
      { attributes: true },
      [handleCartOrQuickviewOpenForCurrency]
    );
  }
  
  if (domCache.cartPage) {
    // theme amendment - observe the STABLE PARENT of #CartPage, not #CartPage itself. The base
    // theme's discount-code component (CartDiscountCode.updateCartContent in main-a17IIrZU.js)
    // updates the cart drawer via cartDrawer.replaceChildren(...) (same node, children swapped -
    // safe to observe directly), but updates the cart PAGE via cart.replaceWith(...) - which
    // destroys the original #CartPage node and drops a new one in its place. An observer attached
    // directly to the original #CartPage node is left watching a dead, detached element after
    // that and never fires again, so currency conversion (and anything else relying on this
    // observer) silently stops working for the rest of the page view after a discount code is
    // applied/removed. The parent element isn't touched by replaceWith, so it keeps observing
    // correctly no matter how many times #CartPage itself gets swapped out.
    observerRegistry.create(
      domCache.cartPage.parentElement || domCache.cartPage,
      { childList: true, subtree: true },
      [handleCartPageMutations]
    );
    // end theme amendment

    // Initial cart data fetch for cart page
    cartManager.fetchCartData().catch(() => {});
  }
  
  if (domCache.productsGrid) {
    observerRegistry.create(
      domCache.productsGrid,
      { childList: true, subtree: true },
      [handleProductsGridMutations]
    );
  }
  
  if (domCache.searchSidebar) {
    observerRegistry.create(
      domCache.searchSidebar,
      { attributes: true },
      [handleSearchSidebarOpen]
    );
  }

  const stickyBuyButton = document.querySelector('.product-form__sticky-buy-button');
  if (stickyBuyButton) {
    observerRegistry.create(
      stickyBuyButton,
      { attributes: true, attributeFilter: ['class'] },
      [() => updateChatWidgetVisibility()]
    );
  }

    document.querySelectorAll('product-recommendations').forEach(function(block) {
    var mo = new MutationObserver(function(mutations, observer) {
      var hasNewNodes = mutations.some(function(m) { return m.addedNodes.length > 0; });
      if (!hasNewNodes) return;
      observer.disconnect();
      // Content has been AJAX-injected — now watch for viewport entry
      new IntersectionObserver(function(entries, obs) {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          currencyManager.refresh();
        }
      }, { threshold: 0.01 }).observe(block);
    });
    mo.observe(block, { childList: true, subtree: false });
  });
  
  if (domCache.announcementBar) {
    observerRegistry.create(
      domCache.announcementBar,
      { childList: true, subtree: true, characterData: true },
      [formatAnnouncementBarPrices]
    );
    
    // Initial formatting
    getAllElements('.announcement-bar__rte .money').forEach(element => {
      applyDutyVatMultiplierToThreshold(element); // theme amendment
      const price = element.textContent.trim();
      const match = price.match(/^([^\d]*\d+)\.\d{2}$/);
      if (match) {
        element.textContent = match[1];
      }
    });
  }
  
  // More selective body observer - only watch for specific elements
  observerRegistry.create(
    domCache.body,
    { childList: true, subtree: true },
    [handleBodyMutations]
  );
  
  // Set up event delegation
  setupCartEventDelegation();
  
  // Initialize remaining functionality
  updateExVatPrice();
  hideYmqAttribution();
  
  // Add consolidated event listeners using event delegation
  document.addEventListener('click', function(event) {
    // Handle variant buttons in quickview
    if (event.target.closest('.product-variant-picker__pill-list label.pill') && 
        document.querySelector('#Quick-view.is-opened')) {
      currencyManager.refreshWithDelay();
    }
    
    // Handle variant buttons on product pages
    if (event.target.closest('label[for^="template"]')) {
      setTimeout(updateExVatPrice, 100);
    }
    
    // Handle quick view buttons
    if (event.target.closest('button[data-quick-view-button]')) {
      setTimeout(updateExVatPrice, 500);
    }

        // Trigger currency conversion when a product-details tab or accordion is opened,
    // since .money elements inside hidden panels may not have been converted on load.
    if (event.target.closest('product-details [data-tabs-name]') ||
        event.target.closest('product-details accordeon-button')) {
      currencyManager.refreshWithDelay(150);
    }
  });

  // UPDATE CURRENCY WHEN CHANGE VARIANT (product page and quickview)
  //
  // Product page: Aurora replaces individual [data-product-block] children inside a stable
  // product-info element — observe product-info for new .money nodes.
  //
  // Quickview: Aurora's VariantPicker calls quickView.setProductFromHTML() which does
  // $el('.product__info-wrapper', this.element).replaceWith(newWrapper) — the entire
  // .product__info-wrapper (including product-info) is detached. Detect quickview via
  // event.target.closest('#Quick-view') (reliable), then observe the stable PARENT of
  // .product__info-wrapper for the replacement to land.
  document.addEventListener('change', function(event) {
    const variantSelector = event.target.closest('[data-variant-picker-option-inputs]');
    if (!variantSelector) return;

    const inQuickView = !!event.target.closest('#Quick-view');

    if (inQuickView) {
      const wrapper = event.target.closest('.product__info-wrapper');
      const observeTarget = wrapper ? wrapper.parentElement : domCache.quickView;

      const observer = new MutationObserver(function(mutations, obs) {
        const hasNewWrapper = mutations.some(mutation =>
          mutation.type === 'childList' &&
          Array.from(mutation.addedNodes).some(node =>
            node.nodeType === 1 && (
              node.classList?.contains('product__info-wrapper') ||
              (node.querySelector && node.querySelector('.product__info-wrapper'))
            )
          )
        );
        if (hasNewWrapper) {
          obs.disconnect();
          currencyManager.refresh();
        }
      });
      observer.observe(observeTarget, { childList: true, subtree: true });
      setTimeout(() => { observer.disconnect(); currencyManager.refresh(); }, 2000);

    } else {
      const productInfo = event.target.closest('product-info');
      if (productInfo) {
        const observer = new MutationObserver(function(mutations, obs) {
          const hasPriceNodes = mutations.some(mutation =>
            mutation.type === 'childList' &&
            Array.from(mutation.addedNodes).some(node =>
              node.nodeType === 1 && (
                node.classList?.contains('money') ||
                (node.querySelector && node.querySelector('.money'))
              )
            )
          );
          if (hasPriceNodes) {
            obs.disconnect();
            currencyManager.refresh();
          }
        });
        observer.observe(productInfo, { childList: true, subtree: true });
        setTimeout(() => { observer.disconnect(); currencyManager.refresh(); }, 2000);
      } else {
        currencyManager.refreshWithDelay(700);
      }
    }
  });

  // Run initial check
  hideYmqLockPasscode();
  
  // Initialize Google Analytics menu tracking
  setTimeout(() => menuTracking.initializeTracking(), 1000);
  
  // Initialize menu hiding for Collections and Eras (desktop only)
  function hideTextMenuItems() {
    if (window.innerWidth < 990) return;
    // CSS already hides .mega-menu__columns at >=990px; just clear any inline override
    const menus = [
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-collections"]'),
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-eras"]')
    ];
    menus.forEach(menu => {
      if (menu) {
        const textColumns = menu.querySelector('.mega-menu__columns');
        if (textColumns) {
          textColumns.style.display = '';
        }
      }
    });
  }
  
  function showTextMenuItems() {
    const menus = [
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-collections"]'),
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-eras"]')
    ];
    menus.forEach(menu => {
      if (menu) {
        const textColumns = menu.querySelector('.mega-menu__columns');
        if (textColumns) {
          // Explicit display:block overrides the CSS rule that hides at >=990px
          textColumns.style.display = 'block';
        }
      }
    });
  }
  
  function removeVintageFromTitles() {
    // Only target collection card titles within specific menu containers
    const menuContainers = [
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-collections"]'),
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-eras"]'),
      document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-shop"]'),
      document.querySelector('#DrawerMenu') // Mobile drawer menu
    ];
    
    menuContainers.forEach(container => {
      if (container) {
        const collectionTitles = container.querySelectorAll('.collection-card__title');
        collectionTitles.forEach(title => {
          if (!title.dataset.vintageRemoved) {
            // Remove "Vintage", replace "Fashion" with "Style" (case insensitive, with surrounding spaces cleaned up)
            title.textContent = title.textContent.replace(/\s*vintage\s*/gi, ' ').replace(/\s*fashion\s*/gi, ' Style ').replace(/\s+/g, ' ').trim();
            title.dataset.vintageRemoved = 'true';
          }
        });
      }
    });
  }
  
  function removeVintageFromCollectionHero() {
    const heroTitle = document.querySelector('.collection-hero__title');
    if (heroTitle && !heroTitle.dataset.vintageRemoved) {
      heroTitle.textContent = heroTitle.textContent.replace(/\s*vintage\s*/gi, ' ').replace(/\s*fashion\s*/gi, ' Style ').replace(/\s+/g, ' ').trim();
      heroTitle.dataset.vintageRemoved = 'true';
    }
  }

  // Set up hover listeners using event delegation
  const header = document.querySelector('header-component') || document.querySelector('header');
  if (header) {
    header.addEventListener('mouseenter', function(e) {
      const trigger = e.target.closest('[data-header-float-element-trigger="HeaderDropdownMenu-collections"], [data-header-float-element-trigger="HeaderDropdownMenu-eras"], [data-header-float-element-trigger="HeaderDropdownMenu-shop"]');
      if (trigger && window.innerWidth >= 990) {
        setTimeout(() => {
          hideTextMenuItems();
          removeVintageFromTitles();
        }, 50);
      }
    }, true);
  }
  
  // Set up observers for menu visibility changes using existing registry
  const collectionsMenu = document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-collections"]');
  const erasMenu = document.querySelector('[data-header-float-element-template="HeaderDropdownMenu-eras"]');
  
  if (collectionsMenu) {
    observerRegistry.create(
      collectionsMenu,
      { attributes: true, attributeFilter: ['aria-hidden', 'class', 'style'] },
      [() => { 
        if (window.innerWidth >= 990) {
          hideTextMenuItems();
          removeVintageFromTitles();
        }
      }]
    );
  }
  
  if (erasMenu) {
    observerRegistry.create(
      erasMenu,
      { attributes: true, attributeFilter: ['aria-hidden', 'class', 'style'] },
      [() => { 
        if (window.innerWidth >= 990) {
          hideTextMenuItems();
          removeVintageFromTitles();
        }
      }]
    );
  }
  
  // Initial hide and title cleanup
  hideTextMenuItems();
  removeVintageFromTitles();
  removeVintageFromCollectionHero();

  const menuResizeHandler = utils.throttle(() => {
    if (window.innerWidth < 990) {
      showTextMenuItems();
    } else {
      hideTextMenuItems();
    }
  }, 250);
  
  window.addEventListener('resize', menuResizeHandler);
});

// WHOLESALE AND UK LOCALIZATION FUNCTIONALITY
document.addEventListener('DOMContentLoaded', function() {
  // Integrated function to handle both wholesale cart clearing and localization
  function handleCustomerSpecificLogic() {
    const hasWholesaleTag = typeof window.hasWholesaleTag === 'boolean' ? window.hasWholesaleTag : 
                           (window.customerTags && window.customerTags.includes('wholesale'));
    
    // Get customer's saved address country code
    const customerAddressCountry = typeof window.customerAddressCountry !== 'undefined' ? window.customerAddressCountry : null;
    
    // Get current localization country code
    const currentLocalizationCountry = typeof window.customerCountryCode !== 'undefined' ? window.customerCountryCode : 
                                      (window.Shopify && window.Shopify.locale && window.Shopify.locale.country) ? window.Shopify.locale.country : 
                                      (window.Shopify && window.Shopify.country) ? window.Shopify.country : null;
    
    const cartAlreadyCleared = utils.getCookie('cartCleared');
    const localizationAttempted = sessionStorage.getItem('localization_attempted');
    
    // CASE 1: Clear cart for wholesale users and set localization based on their address
    if (hasWholesaleTag && !cartAlreadyCleared) {
      return fetch(utils.cartRoute('cart/clear.js'), { // theme amendment - localized Ajax API path, see utils.cartRoute
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
      .then(response => response.json())
      .then(data => {
        utils.setCookie('cartCleared', 'true', 1);
        
        // Set localization based on customer's address country
        if (customerAddressCountry && !localizationAttempted) {
          // Check if already using the correct localization
          const alreadyCorrectLocalization = currentLocalizationCountry === customerAddressCountry || 
                                            document.cookie.includes(`localization=${customerAddressCountry}`) || 
                                            window.location.href.includes(`/localization=${customerAddressCountry}`);
          
          if (alreadyCorrectLocalization) {
            location.reload();
          } else {
            setCustomerLocalization(customerAddressCountry);
          }
        } else {
          // No address country available, just reload
          location.reload();
        }
      })
      .catch(error => {
        // Even if cart clear fails, try localization if needed
        if (customerAddressCountry && !localizationAttempted) {
          setCustomerLocalization(customerAddressCountry);
        }
      });
    }
    
    // CASE 2: Set localization based on address if not already done (for non-wholesale or already cleared cart)
    else if (hasWholesaleTag && customerAddressCountry && !localizationAttempted) {
      const alreadyCorrectLocalization = currentLocalizationCountry === customerAddressCountry || 
                                        document.cookie.includes(`localization=${customerAddressCountry}`) || 
                                        window.location.href.includes(`/localization=${customerAddressCountry}`);
      
      if (!alreadyCorrectLocalization) {
        return setCustomerLocalization(customerAddressCountry);
      }
    }
  }
  
  // Function to set localization to customer's address country
  function setCustomerLocalization(countryCode) {
    if (!countryCode) {
      return;
    }
    
    sessionStorage.setItem('localization_attempted', 'true');
    
    // Create and submit form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.style.display = 'none';
    
    const inputs = [
      {name: 'form_type', value: 'localization'},
      {name: 'utf8', value: '✓'},
      {name: '_method', value: 'put'},
      {name: 'country_code', value: countryCode},
      {name: 'return_to', value: window.location.pathname}
    ];
    
    inputs.forEach(input => {
      const inputElem = document.createElement('input');
      inputElem.type = 'hidden';
      inputElem.name = input.name;
      inputElem.value = input.value;
      form.appendChild(inputElem);
    });
    
    document.body.appendChild(form);
    form.submit();
  }
  
  // Run the integrated logic after DOM is fully loaded
  setTimeout(handleCustomerSpecificLogic, 1000);
  
/*
// Redirect wholesale users after adding products to cart
  var currentUrl = new URL(window.location.href);
  var currentPathname = currentUrl.pathname;
  
  var referrerUrl, referrerPathname;
  try {
      referrerUrl = new URL(document.referrer);
      referrerPathname = referrerUrl.pathname;
  } catch (e) {
      referrerUrl = null;
      referrerPathname = '';
  }
  
  if (referrerPathname === currentPathname && currentUrl.search.includes('mvact=addedtocart')) {
      window.location.href = '/collections/wholesale?view=cart';
  }
  */
});


// Open cart drawer for wholesale views
window.addEventListener('load', function() {
  if (window.location.pathname.includes('/collections/wholesale') && window.location.search.includes('view=cart')) {
      var cartButton = document.querySelector('button[data-sidebar-id="CartDrawer"]');
      if (cartButton) {
        cartButton.click();
      }
  }
});




  









/* WHOLESALE REDIRECT TO THANK YOU PAGE INSTEAD OF CHECKOUT */
(function() {
    // Check if customer has wholesale tag AND either europe OR usa tag
    function isWholesaleExportCustomer() {
        if (typeof window.customerTags === 'undefined') return false;
        
        let tags = [];
        if (Array.isArray(window.customerTags)) {
            tags = window.customerTags;
        } else if (typeof window.customerTags === 'string') {
            try {
                tags = JSON.parse(window.customerTags.replace(/&quot;/g, '"'));
            } catch (e) {
                tags = [window.customerTags];
            }
        }
        
        // Must have wholesale tag AND either europe OR usa tag
        const hasWholesale = tags.includes('wholesale');
        const hasEurope = tags.includes('europe');
        const hasUsa = tags.includes('usa');
        
        return hasWholesale && (hasEurope || hasUsa);
    }
    
    // Only proceed if customer qualifies for export wholesale flow
    if (!isWholesaleExportCustomer()) {
        return;
    }
    
    // Wait for the YmqB2b instance to be ready
    const checkInterval = setInterval(() => {
        if (window.ymqB2b && typeof window.ymqB2b.doCheckout === 'function') {
            clearInterval(checkInterval);
            
            // Store the original doCheckout method
            const originalDoCheckout = window.ymqB2b.doCheckout;
            
            // Override the doCheckout method
            window.ymqB2b.doCheckout = function(checkOutDom = null, cart_form = null) {
                const that = this;
                
                // Run all the validation checks from original method
                if (that.taxexempt != null && 
                    that.taxexempt.vat_exempt_required == 1 && 
                    that.taxexempt.vat_exempt_check != "" && 
                    ymqBq(".ymq-b2b-cart-tax-display-box").length != 0 && 
                    that.vat_success == 0) {
                    that._vatError();
                    return false;
                }
                
                if (that.cart_ol_error.length > 0) {
                    let html = "";
                    that.cart_ol_error.forEach(error => {
                        let left_html = "";
                        let right_html = "";
                        error.items.forEach(item => {
                            let variant_title = item.item.variant_title == null ? "" : ` - ${item.item.variant_title}`;
                            left_html += `<div class="ymq-b2b-ol-msg-left-item">${item.item.product_title}${variant_title}</div>`;
                        });
                        error.errors.forEach(err => {
                            right_html += `<div class="ymq-b2b-ol-msg-right-item">${err}</div>`;
                        });
                        html += `<div class="ymq-b2b-ol-msg-line">
                            <div class="ymq-b2b-ol-msg-item ymq-b2b-ol-msg-left">${left_html}</div>
                            <div class="ymq-b2b-ol-msg-item ymq-b2b-ol-msg-right">${right_html}</div>
                        </div>`;
                    });
                    ymqBq(".ymq-b2b-pl-msg-box").html(html);
                    that.ol_modal.modal("show");
                    that._endLoading(that.checkOutDom);
                    return false;
                }
                
                if (checkOutDom != null) {
                    that.checkOutDom = checkOutDom;
                }
                if (cart_form != null) {
                    that.cart_form = cart_form;
                }
                
                that._startLoading(that.checkOutDom);
                that.getAttributes();
                
                let note = "";
                that.cart_form.find('[name="note"]').each(function() {
                    note += `${ymqBq(this).val()}\n`;
                });
                
                if (that.vat_success) {
                    note += `${that.selected_vat_info.title}: ${that.selected_vat_info.value}\n`;
                    that.note_attributes.push({
                        name: that.selected_vat_info.title,
                        value: that.selected_vat_info.value
                    });
                }

                // Add customer information as note attributes (displayed in Additional Details panel)
                if (typeof window.customerInfo !== 'undefined' && window.customerInfo) {
                    that.note_attributes.push({
                        name: 'Customer Name',
                        value: window.customerInfo.name || ''
                    });
                    that.note_attributes.push({
                        name: 'Customer Email',
                        value: window.customerInfo.email || ''
                    });
                    that.note_attributes.push({
                        name: 'Customer ID',
                        value: String(window.customerInfo.id || '')
                    });
                }
                
                // Add Order Type as note attribute for Shopify Flow automation
                that.note_attributes.push({
                    name: 'Order Type',
                    value: 'wholesale_draft'
                });
                
                that.checkoutAjaxData = {
                    data: that.cart,
                    note: note,
                    allowDiscountCodesInCheckout: that.allowDiscountCodesInCheckout ? 1 : 0,
                    currency: Shopify.currency && Shopify.currency.rate && Shopify.currency.active,
                    country: ymq_b2b.country,
                    rate: Shopify.currency && Shopify.currency.active && Shopify.currency.rate,
                    note_attributes: that.note_attributes,
                    cart_price: that.cart_price_info,
                    order_discount: that.order_discount,
                    ef: that.cart_ef_info,
                    ef_free_shipping: that.cart_ef_free_shipping ? 1 : 0,
                    option_extra: that._optionExtra(),
                    customer_tax_exempt: that.customer_tax_exempt ? 1 : 0,
                    taxes_included: ymq_b2b.shop.taxes_included ? 1 : 0
                };
                
                if (that.cart_ef_free_shipping == 0 && 
                    that.cart_sr_info.hasOwnProperty("ship_price") && 
                    that.cart_sr_info.hasOwnProperty("sr")) {
                    that.checkoutAjaxData.ship_price = that.cart_sr_info.ship_price;
                    that.checkoutAjaxData.ship_price_name = that.cart_sr_info.sr[0].label != "" ? 
                        that.cart_sr_info.sr[0].label : that.cart_sr_info.sr[0].name;
                }
                
                // Make the API call to create draft order
                ymqBq.post(`${that.prefix_url}checkout`, that.checkoutAjaxData)
                    .done(function(response) {
                        setTimeout(() => {
                            const customerEmail = window.customerInfo?.email || '';
                            const createdAt = new Date().toISOString();
                            const totalPrice = that.cart.total_price || '';
                            const referenceId = response.msg || '';

                            const params = new URLSearchParams({
                                ref: referenceId,
                                email: customerEmail,
                                date: createdAt,
                                total: totalPrice
                            });

                            // Clear the cart before redirecting
                            fetch(utils.cartRoute('cart/clear.js'), { // theme amendment - localized Ajax API path, see utils.cartRoute
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                credentials: 'same-origin'
                            })
                            .then(() => {
                                that._endLoading(that.checkOutDom);
                                window.location.href = `/pages/wholesale-order-confirmation?${params.toString()}`;
                            })
                            .catch(() => {
                                that._endLoading(that.checkOutDom);
                                window.location.href = `/pages/wholesale-order-confirmation?${params.toString()}`;
                            });
                        }, that._getConfig("after_checkout_time", 0));
                    })
                    .fail(function(error) {
                        that._endLoading(that.checkOutDom);
                        alert('There was an error creating your order. Please try again or contact support.');
                    });
                    
                return false;
            };
        }
    }, 100);
    
    setTimeout(() => clearInterval(checkInterval), 10000);
})();

// Fix: mobile carousel arrow buttons use transform instead of native scroll.
// On mobile, .slider-grid__m-carousel uses overflow:auto + scroll-snap, so
// scrollTo() must call track.scrollTo() rather than applying a CSS transform.
(function () {
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-direction]');
    if (!btn) return;
    const sliderGrid = btn.closest('slider-grid');
    if (!sliderGrid || sliderGrid._mobileScrollFixed) return;

    sliderGrid._mobileScrollFixed = true;
    sliderGrid.scrollTo = function (value) {
      if (this.isMobile) {
        this.track.removeAttribute('style');
        this.track.scrollTo({ left: Math.abs(value), behavior: 'smooth' });
      } else {
        this.track.setAttribute('style', 'transform: translateX(' + value + 'px);');
      }
      this.toggleNavItems();
    };
  }, true); // true = capture phase, runs before slider-grid's bubble handler
})();

// Fix: the dynamic sticky header (header.liquid is-sticky/is-sticky-mobile = "dynamic") pauses with
// an empty header-height gap - the real menu sitting half-way down the page - for a moment before
// snapping back into its normal spot when scrolling up to the very top. Cause: header-component's own
// scroll handler only re-checks scroll position every 200ms (throttled), so removing the "scrolled"
// class that switches the header from position:sticky back to position:static lags behind the user
// physically reaching the top; and once it is removed, .shopify-section-header's own
// `transition: transform .3s ease-in-out` (main-BVJ_5JXW.css) animates the leftover translateY back
// to 0 instead of snapping - together that's the "pause". Forcing header-component's existing
// setScrollStatus() to run the instant scrollY reaches 0 (instead of waiting for its throttle), with
// the transform transition briefly disabled (see override.css .header--snap-to-top), makes it settle
// into place immediately on both mobile and desktop.
(function () {
  let atTop = true;

  window.addEventListener('scroll', function () {
    if (window.scrollY > 0) {
      atTop = false;
      return;
    }
    if (atTop) return;
    atTop = true;

    const header = document.querySelector('header-component');
    const section = document.querySelector('.shopify-section-header');
    if (!header || !section || !section.classList.contains('scrolled')) return;
    if (typeof header.setScrollStatus !== 'function') return;

    section.classList.add('header--snap-to-top');
    header.setScrollStatus();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        section.classList.remove('header--snap-to-top');
      });
    });
  }, { passive: true });
})();
